'use client'
import React, { createContext, useContext, useState, useEffect } from 'react'
import { translations } from '@/constants/translations'
import { wikiTranslations } from '@/constants/translations-wiki'
import { updateTranslations } from '@/constants/updateTranslations'
import { blogTranslations } from '@/constants/blogTranslations'
import { landingTranslations } from '@/constants/landingTranslations'
import { DEFAULT_LANGUAGE, LANGUAGE_COOKIE, isSupported } from '@/utils/locale'
import { track } from '@/utils/analytics'

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1 año

function writeLanguageCookie(lang) {
    if (typeof document === 'undefined') return
    // Sin HttpOnly a propósito: la escribe el cliente. No es un secreto, es una
    // preferencia de UI. SameSite=Lax alcanza.
    document.cookie = `${LANGUAGE_COOKIE}=${lang}; path=/; max-age=${COOKIE_MAX_AGE}; samesite=lax`
}

// Combinar todas las traducciones
const allTranslations = { ...translations, ...wikiTranslations, ...updateTranslations, ...blogTranslations, ...landingTranslations }

const LanguageContext = createContext()

export const LanguageProvider = ({ children, initialLanguage = DEFAULT_LANGUAGE }) => {
    // El idioma YA viene resuelto del servidor (app/layout.js → utils/locale.js:
    // cookie explícita > Accept-Language > inglés). Arrancar con ese valor es lo
    // que evita el parpadeo: el primer render del cliente coincide exactamente
    // con el HTML que mandó el servidor.
    const [language, setLanguage] = useState(initialLanguage)
    const [isHydrated, setIsHydrated] = useState(false)

    useEffect(() => {
        // Migración de usuarios viejos: antes la elección vivía sólo en
        // localStorage, que el servidor no puede leer. Si hay una guardada y no
        // coincide con lo que resolvió el servidor, la respetamos y la
        // escribimos como cookie para que el próximo SSR ya salga bien.
        //
        // Ojo: acá NO se vuelve a mirar navigator.language. El servidor ya
        // negoció con Accept-Language; repetirlo en el cliente sólo podría
        // pisar una preferencia explícita.
        const saved = localStorage.getItem(LANGUAGE_COOKIE)
        if (isSupported(saved) && saved !== language) {
            setLanguage(saved)
        }
        if (isSupported(saved)) writeLanguageCookie(saved)

        setIsHydrated(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const changeLanguage = (lang) => {
        if (!isSupported(lang)) return
        setLanguage(lang)
        localStorage.setItem(LANGUAGE_COOKIE, lang)
        // La cookie es lo único que el servidor puede leer: sin esto, la
        // elección se perdería en la próxima carga y volvería a negociar.
        writeLanguageCookie(lang)
        track('feature_use', { feature: 'language', value: lang })
    }

    const t = (key) => {
        if (!allTranslations[key]) {
            console.warn(`Translation key not found: ${key}`)
            return key
        }
        return allTranslations[key][language] || allTranslations[key]['en']
    }

    return (
        <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, t, isHydrated }}>
            {children}
        </LanguageContext.Provider>
    )
}

export const useLanguage = () => {
    const context = useContext(LanguageContext)
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider')
    }
    return context
}
